import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { captureMarketingVisit } from '../utils/marketingAttribution';

function Home() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showTuningPanel = searchParams.get('heroTune') === '1';

  useEffect(() => {
    captureMarketingVisit({
      search: window.location.search,
      referrer: document.referrer,
      landingPath: window.location.pathname,
    }).catch(() => {
      // Public marketing tracking is best-effort and never blocks navigation.
    });
  }, []);

  // Hero layout tuning state
  const [heroVars, setHeroVars] = useState({
    heroPortraitWidth: '0px',
    heroPortraitHeight: '0px',
    heroTextWidthMin: '0px',
    heroTextWidthMax: '0px',
    heroDashboardWidthMin: '520px',
    heroDashboardHeight: '345px',
    heroDashboardRightPadding: '0',
    heroDashboardTopOffset: '0',
    heroColumnGap: '32px',
    heroBadgeWidth: 'auto',
    heroBadgeTopMargin: '0',
    heroTextTopOffset: '0',
  });

  const updateVar = (key, value) => {
    setHeroVars(prev => ({ ...prev, [key]: value }));
  };

  const getCSSOutput = () => {
    return `:root {
  /* Hero container spacing */
  --hero-left-padding: 0;
  --hero-right-padding: 0;
  --hero-column-gap: ${heroVars.heroColumnGap};
  
  /* Hero zone widths */
  --hero-portrait-width: ${heroVars.heroPortraitWidth};
  --hero-portrait-height: ${heroVars.heroPortraitHeight};
  --hero-text-width-min: ${heroVars.heroTextWidthMin};
  --hero-text-width-max: ${heroVars.heroTextWidthMax};
  --hero-dashboard-width-min: ${heroVars.heroDashboardWidthMin};
  
  /* Hero vertical offsets */
  --hero-portrait-top-offset: 0;
  --hero-text-top-offset: ${heroVars.heroTextTopOffset};
  --hero-dashboard-top-offset: ${heroVars.heroDashboardTopOffset};
  
  /* Dashboard sizing */
  --hero-dashboard-height: ${heroVars.heroDashboardHeight};
  --hero-dashboard-right-padding: ${heroVars.heroDashboardRightPadding};
  
  /* Badge strip */
  --hero-badge-width: ${heroVars.heroBadgeWidth};
  --hero-badge-top-margin: ${heroVars.heroBadgeTopMargin};
}`;
  };

  const heroStyle = {
    '--hero-bg-image': `url("${process.env.PUBLIC_URL}/assets/hero-bg-desktop.webp")`,
    '--hero-bg-image-mobile': `url("${process.env.PUBLIC_URL}/assets/hero-bg-mobile.webp")`,
    ...(showTuningPanel ? {
      '--hero-portrait-width': heroVars.heroPortraitWidth,
      '--hero-portrait-height': heroVars.heroPortraitHeight,
      '--hero-text-width-min': heroVars.heroTextWidthMin,
      '--hero-text-width-max': heroVars.heroTextWidthMax,
      '--hero-dashboard-width-min': heroVars.heroDashboardWidthMin,
      '--hero-dashboard-height': heroVars.heroDashboardHeight,
      '--hero-dashboard-right-padding': heroVars.heroDashboardRightPadding,
      '--hero-dashboard-top-offset': heroVars.heroDashboardTopOffset,
      '--hero-column-gap': heroVars.heroColumnGap,
      '--hero-badge-width': heroVars.heroBadgeWidth,
      '--hero-badge-top-margin': heroVars.heroBadgeTopMargin,
      '--hero-text-top-offset': heroVars.heroTextTopOffset,
    } : {})
  };

  return (
    <div className="public-page" style={heroStyle}>
      {/* Navigation - Premium Compact Header */}
      <nav className="public-nav">
        <div className="public-nav-container">
          <Link to="/" className="public-nav-brand">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="nav-brand-logo" 
            />
          </Link>
          <div className="public-nav-links">
            <a href="#features" className="public-nav-link">Features</a>
            <a href="#how-it-works" className="public-nav-link">How It Works</a>
            <Link to="/pricing" className="public-nav-link">Pricing</Link>
            <Link to="/login" className="public-nav-link nav-link-login">Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Image-Based Background with Overlay Buttons */}
      <div className="hero-section">
        <div className="hero-button-overlay">
          <Link to="/signup" className="btn btn-hero-primary">
            Explore the Platform
          </Link>
      
        </div>
      </div>

      {/* Features Section - Compact Cards Under Hero */}
      <div className="features-section" id="features">
        <div className="page-container">
          <div className="features-horizontal">
            <div className="feature-card-horizontal">
              <div className="feature-icon-circle">
                <svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z"></path><path d="m8.5 12 2.25 2.25L15.5 9.5"></path>
                </svg>
              </div>
              <div className="feature-content-horizontal">
                <h3 className="feature-title-horizontal">Disciplined Analysis</h3>
                <p className="feature-description-horizontal">
                  Rule-based screening
                </p>
              </div>
            </div>

            <div className="feature-card-horizontal">
              <div className="feature-icon-circle">
                <svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="8"></circle><circle cx="12" cy="12" r="3"></circle><path d="M12 2v2M12 20v2M2 12h2M20 12h2"></path>
                </svg>
              </div>
              <div className="feature-content-horizontal">
                <h3 className="feature-title-horizontal">Long-Term Focus</h3>
                <p className="feature-description-horizontal">
                  Value over momentum
                </p>
              </div>
            </div>

            <div className="feature-card-horizontal">
              <div className="feature-icon-circle">
                <svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z"></path><path d="M9 12h6M12 9v6"></path>
                </svg>
              </div>
              <div className="feature-content-horizontal">
                <h3 className="feature-title-horizontal">Independent</h3>
                <p className="feature-description-horizontal">
                  Unbiased objective research
                </p>
              </div>
            </div>

            <div className="feature-card-horizontal">
              <div className="feature-icon-circle">
                <svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="10" width="14" height="10" rx="2"></rect><path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
                </svg>
              </div>
              <div className="feature-content-horizontal">
                <h3 className="feature-title-horizontal">Secure &amp; Private</h3>
                <p className="feature-description-horizontal">
                  Your data stays yours
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section - Compact with Gold Divider */}
      <div className="how-it-works-section" id="how-it-works">
        <div className="page-container">
          <div className="section-divider"></div>
          <h2 className="section-title-compact">How It Works</h2>
          <div className="features-horizontal how-it-works-products">
            <div className="feature-card-horizontal"><div className="feature-icon-circle"><svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg></div><div className="feature-content-horizontal"><h3 className="feature-title-horizontal">Stock Screening</h3><p className="feature-description-horizontal">Screen U.S. and Canadian indexed stocks using financial, valuation, quality, and risk filters.</p></div></div>
            <div className="feature-card-horizontal"><div className="feature-icon-circle"><svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg></div><div className="feature-content-horizontal"><h3 className="feature-title-horizontal">Stock Research &amp; Backtesting</h3><p className="feature-description-horizontal">Review fundamentals, rankings, and historical results for individual stocks.</p></div></div>
            <div className="feature-card-horizontal"><div className="feature-icon-circle"><svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg></div><div className="feature-content-horizontal"><h3 className="feature-title-horizontal">Strategy Lab</h3><p className="feature-description-horizontal">Test Top N portfolio sizes, compare historical strategies, and build a practical portfolio plan.</p><Link to="/pricing" className="feature-link">Learn more →</Link></div></div>
            <div className="feature-card-horizontal"><div className="feature-icon-circle"><svg className="feature-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg></div><div className="feature-content-horizontal"><h3 className="feature-title-horizontal">Portfolio &amp; Alerts</h3><p className="feature-description-horizontal">Build and balance portfolios, track holdings, and receive alerts when rankings or key metrics change.</p><Link to="/pricing" className="feature-link">Learn more →</Link></div></div>
          </div>
        </div>
      </div>

      {/* Pricing CTA Bar - compact premium strip */}
      <div className="cta-bar-premium">
        <div className="page-container cta-page-container">
          <div className="cta-bar-premium-content">
            
            <div className="cta-text-area">
              <h2 className="cta-premium-title">Research smarter.</h2>
              <p className="cta-premium-subtitle">Join Black Peak Analytics today and start finding value beyond the price.</p>
            </div>
            
            <div className="cta-action-area">
              <Link to="/signup" className="btn btn-cta-premium">
               Create a Free Account <span aria-hidden="true">›</span>
              </Link>
              <span className="cta-small-note">No credit card required.</span>
            </div>
          </div>
        </div>
      </div>


      {/* Hero Layout Tuning Panel */}
      {showTuningPanel && (
        <div className="hero-tuning-panel">
          <div className="tuning-panel-header">
            <h3>Hero Layout Tuning</h3>
            <button 
              className="tuning-close-btn"
              onClick={() => window.location.href = window.location.pathname}
            >
              ✕
            </button>
          </div>
          
          <div className="tuning-controls">
            <div className="tuning-group">
              <label>Portrait Width</label>
              <input 
                type="text" 
                value={heroVars.heroPortraitWidth}
                onChange={(e) => updateVar('heroPortraitWidth', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Portrait Height</label>
              <input 
                type="text" 
                value={heroVars.heroPortraitHeight}
                onChange={(e) => updateVar('heroPortraitHeight', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Text Width Min</label>
              <input 
                type="text" 
                value={heroVars.heroTextWidthMin}
                onChange={(e) => updateVar('heroTextWidthMin', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Text Width Max</label>
              <input 
                type="text" 
                value={heroVars.heroTextWidthMax}
                onChange={(e) => updateVar('heroTextWidthMax', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Dashboard Width Min</label>
              <input 
                type="text" 
                value={heroVars.heroDashboardWidthMin}
                onChange={(e) => updateVar('heroDashboardWidthMin', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Dashboard Height</label>
              <input 
                type="text" 
                value={heroVars.heroDashboardHeight}
                onChange={(e) => updateVar('heroDashboardHeight', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Dashboard Right Padding</label>
              <input 
                type="text" 
                value={heroVars.heroDashboardRightPadding}
                onChange={(e) => updateVar('heroDashboardRightPadding', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Dashboard Top Offset</label>
              <input 
                type="text" 
                value={heroVars.heroDashboardTopOffset}
                onChange={(e) => updateVar('heroDashboardTopOffset', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Column Gap</label>
              <input 
                type="text" 
                value={heroVars.heroColumnGap}
                onChange={(e) => updateVar('heroColumnGap', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Badge Width</label>
              <input 
                type="text" 
                value={heroVars.heroBadgeWidth}
                onChange={(e) => updateVar('heroBadgeWidth', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Badge Top Margin</label>
              <input 
                type="text" 
                value={heroVars.heroBadgeTopMargin}
                onChange={(e) => updateVar('heroBadgeTopMargin', e.target.value)}
              />
            </div>

            <div className="tuning-group">
              <label>Text Top Offset</label>
              <input 
                type="text" 
                value={heroVars.heroTextTopOffset}
                onChange={(e) => updateVar('heroTextTopOffset', e.target.value)}
              />
            </div>
          </div>

          <div className="tuning-output">
            <h4>CSS Output (copy to App.css)</h4>
            <pre>{getCSSOutput()}</pre>
          </div>
        </div>
      )}

      {/* Footer - compact target-style footer */}
      <footer className="public-footer public-footer-compact">
        <div className="public-footer-container public-footer-container-compact">
          <Link to="/" className="footer-brand-compact">
            <img 
              src="/assets/black_peak_analytics_logo_transparent_240.png" 
              alt="Black Peak Analytics" 
              className="footer-brand-logo" 
            />
          </Link>
          <nav className="footer-nav-compact" aria-label="Footer navigation">
            <a href="#features" className="public-footer-link">Features</a>
            <a href="#how-it-works" className="public-footer-link">How It Works</a>
            <Link to="/pricing" className="public-footer-link">Pricing</Link>
            <Link to="/support" className="public-footer-link">Support</Link>
            <Link to="/privacy" className="public-footer-link">Privacy</Link>
            <Link to="/terms" className="public-footer-link">Terms</Link>
            <Link to="/investment-disclaimer" className="public-footer-link">Disclaimer</Link>
          </nav>
          <p className="footer-disclaimer footer-disclaimer-compact">Independent research tool. Not financial advice.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
